// The Dashboard's view model (WEB_PLAN.md §5.3), React-free so it can be unit-tested: the period switch, the KPI
// numbers and tiles with their Stores-list links, "Team now", the weekly leaderboard card, "Needs attention" and the
// recent activity feed with each entry's photos. Business rules come from domain/ (metrics, periods, pipelines, records,
// team); this module only picks, scopes and words them the way iOS Home and Reports do (TodayKPIStrip, ReportKPIGrid,
// TeamSnapshotSection, RepRow / RepPeekCard, ActivityFeedView, ActivityRow).

import type { ActivityType, GoalMetric, GoalPeriod } from '@/contracts/enums';
import { ACTIVITY_TYPE_TITLES, USER_ROLE_TITLES } from '@/contracts/enums';
import type {
  ActivityPayload,
  AttachmentPayload,
  PipelinePayload,
  RecordPayload,
  UserPayload,
  VisitResultPayload,
} from '@/contracts/models';
import type { MetricsRow } from '@/contracts/wire';
import { parseWireDate } from '@/contracts/wireDate';
import type { DataItem } from '@/data/lists';
import { calendarDaysBetween, isSameDay } from '@/domain/calendar';
import { effectiveDate, type DerivedStats } from '@/domain/derived';
import {
  formatCompactMoney,
  formatElapsed,
  formatGrouped,
  formatPercent,
  formatRelative,
  formatShortDateTime,
  formatShortTime,
} from '@/domain/format';
import { isAppointmentResult, metricsRowValue, reportSummary, scopedRecords, teamFeed, type ReportData } from '@/domain/metrics';
import type { DateRange } from '@/domain/periods';
import { pipelineTotals } from '@/domain/pipelines';
import {
  EMPTY_FILTER,
  activityDisplayTitle,
  atRiskRecords,
  filterToQuery,
  overdueActivities,
  recordDisplayName,
  type MapFilter,
} from '@/domain/records';
import { PRESENCE_ONLINE_MS, presenceOf, type Presence, type RepPosition } from '@/domain/team';
import { compareLocalized, fullName, hasId, idKey, idSet, lookup, ms, sameId, userName } from '@/domain/util';
import { attachmentCountText } from '@/features/stores/detailModel';

// MARK: - Period switch

/** The four dashboard periods, in the switch's order (`GoalPeriod`, measured from the period start to now). */
export const DASHBOARD_PERIODS: readonly GoalPeriod[] = ['daily', 'weekly', 'monthly', 'quarterly'];

export const DASHBOARD_PERIOD_TITLES: Readonly<Record<GoalPeriod, string>> = {
  daily: 'Today',
  weekly: 'This week',
  monthly: 'This month',
  quarterly: 'This quarter',
};

/** `?period=` values (short words, so a shared link reads well). Today has none: it is the default. */
const PERIOD_PARAMS: Readonly<Record<GoalPeriod, string>> = {
  daily: 'today',
  weekly: 'week',
  monthly: 'month',
  quarterly: 'quarter',
};

export const PERIOD_QUERY_KEY = 'period';

/** Reads `?period=`; anything unknown is Today, the iOS Home default. */
export function periodFromParam(value: string | null | undefined): GoalPeriod {
  const v = (value ?? '').toLowerCase();
  return DASHBOARD_PERIODS.find((p) => PERIOD_PARAMS[p] === v) ?? 'daily';
}

/** The `?period=` value, or null for Today (kept out of the URL). */
export function periodParam(period: GoalPeriod): string | null {
  return period === 'daily' ? null : PERIOD_PARAMS[period];
}

/** "Thu, Oct 1" for today, "Sep 27 – Oct 1" for longer periods (the page subtitle). */
export function periodRangeLabel(period: GoalPeriod, range: DateRange, locale?: string): string {
  const day = (d: Date, withWeekday: boolean) =>
    new Intl.DateTimeFormat(locale, { weekday: withWeekday ? 'short' : undefined, month: 'short', day: 'numeric' }).format(d);
  if (period === 'daily' || isSameDay(range.from, range.to)) return day(range.to, true);
  return `${day(range.from, false)} – ${day(range.to, false)}`;
}

// MARK: - Numbers

/** What the KPI tiles show, from the replica (admins, managers) or from `team_metrics` (reps). */
export interface DashboardNumbers {
  visits: number;
  contacts: number;
  appointments: number;
  newStores: number;
  won: number;
  revenue: number;
  /** Value of open stores (not in a won or lost stage; no stage counts as open). */
  open: number;
  /** Σ value × stage probability / 100 over the open stores. */
  weighted: number;
  openCount: number;
}

export const ZERO_NUMBERS: Readonly<DashboardNumbers> = Object.freeze({
  visits: 0,
  contacts: 0,
  appointments: 0,
  newStores: 0,
  won: 0,
  revenue: 0,
  open: 0,
  weighted: 0,
  openCount: 0,
});

/**
 * Admins and managers: `ReportEngine.summary` over the viewer's visible people for the period (iOS Reports "Key
 * metrics"): completed activities by visible users, stores unowned or owned by them (archived ones left out).
 * Pass only `from` (no `to`), as iOS ranges have no end: the numbers then depend on the period start, not the clock,
 * so the page does not re-read every activity date once a minute.
 */
export function teamNumbers(data: ReportData, range: { from: Date; to?: Date }): DashboardNumbers {
  const filter = range.to === undefined ? { from: range.from } : { from: range.from, to: range.to };
  const s = reportSummary(data, filter);
  const pipeline = pipelineTotals(scopedRecords(data, filter), data.pipelines);
  return {
    visits: s.visits,
    contacts: s.contacts,
    appointments: s.appointments,
    newStores: s.recordsCreated,
    won: s.recordsWon,
    revenue: s.revenueWon,
    open: s.pipelineValue,
    weighted: s.weightedPipeline,
    openCount: pipeline.openCount,
  };
}

const METRIC_COLUMNS = ['visits', 'contacts', 'appointments', 'calls', 'emails', 'texts', 'deals', 'revenue', 'new_records'] as const;

/**
 * The `team_metrics` answer (a jsonb array) as typed rows. The RPC is outside the sync contracts, so anything that is
 * not a row with a `profile_id` is dropped and a missing or non-numeric column reads as 0, rather than reaching the
 * tiles as `undefined` or a string (which would print "$0" or concatenate in sums).
 */
export function decodeMetricsRows(raw: unknown): MetricsRow[] {
  if (!Array.isArray(raw)) return [];
  const out: MetricsRow[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    if (typeof o.profile_id !== 'string' || o.profile_id === '') continue;
    const row = { profile_id: o.profile_id } as MetricsRow;
    for (const key of METRIC_COLUMNS) {
      const v = typeof o[key] === 'number' ? (o[key] as number) : Number(o[key]);
      row[key] = Number.isFinite(v) ? v : 0;
    }
    out.push(row);
  }
  return out;
}

/**
 * What the rep's KPI area shows for the `team_metrics` query: skeletons until the first answer for this period,
 * the error card only when there is nothing to show, and the last numbers with a "Couldn't refresh" note when a
 * background refetch fails (the default `retry: 1` turns a short outage into an error state).
 */
export type RepKpiState = 'loading' | 'ready' | 'stale' | 'error';

export function repKpiState(q: { data: unknown; isError: boolean }): RepKpiState {
  if (q.data !== undefined) return q.isError ? 'stale' : 'ready';
  return q.isError ? 'error' : 'loading';
}

/** The viewer's own row of a `team_metrics` answer (ids compared in any case). */
export function myMetricsRow(rows: readonly MetricsRow[] | null | undefined, profileId: string | null | undefined): MetricsRow | undefined {
  if (!rows || !profileId) return undefined;
  return rows.find((r) => sameId(r.profile_id, profileId));
}

/** A rep's open pipeline: their own, non-archived stores in the replica (their feed always has those). */
export function myPipeline(
  records: readonly RecordPayload[],
  pipelines: readonly PipelinePayload[],
  profileId: string | null | undefined,
): { open: number; weighted: number; openCount: number } {
  if (!profileId) return { open: 0, weighted: 0, openCount: 0 };
  return pipelineTotals(
    records.filter((r) => !r.isArchived && sameId(r.ownerId, profileId)),
    pipelines,
  );
}

/** Reps: the period's numbers from `team_metrics` (no row = nothing logged yet) plus their replica pipeline. */
export function repNumbers(row: MetricsRow | undefined, pipeline: { open: number; weighted: number; openCount: number }): DashboardNumbers {
  return {
    visits: row ? metricsRowValue(row, 'visits') : 0,
    contacts: row ? metricsRowValue(row, 'contacts') : 0,
    appointments: row ? metricsRowValue(row, 'appointments') : 0,
    newStores: row ? metricsRowValue(row, 'newRecords') : 0,
    won: row ? metricsRowValue(row, 'deals') : 0,
    revenue: row ? metricsRowValue(row, 'revenue') : 0,
    open: pipeline.open,
    weighted: pipeline.weighted,
    openCount: pipeline.openCount,
  };
}

// MARK: - Stores list links

/** Where each tile and attention block leads: the Stores list with the closest `MapFilter` in the query. */
export interface StoreLinks {
  visits?: string;
  contacts?: string;
  appointments?: string;
  /** No `MapFilter` criterion selects stores by creation date, so New stores has no link. */
  newStores?: string;
  won?: string;
  pipeline?: string;
  overdue?: string;
  atRisk?: string;
  unassigned?: string;
}

export interface StoreLinkContext {
  range: DateRange;
  now: Date;
  pipelines: readonly PipelinePayload[];
  visitResults: readonly VisitResultPayload[];
}

/** `/stores` plus the filter's query (`filterToQuery`), or plain `/stores` for an empty filter. */
export function storesHref(filter: Partial<MapFilter>): string {
  const query = filterToQuery({ ...EMPTY_FILTER, ...filter }).toString();
  return query === '' ? '/stores' : `/stores?${query}`;
}

function stageIds(pipelines: readonly PipelinePayload[], pick: (s: PipelinePayload['stages'][number]) => boolean): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const p of pipelines) {
    for (const s of p.stages) {
      if (!pick(s) || seen.has(idKey(s.id))) continue;
      seen.add(idKey(s.id));
      out.push(s.id);
    }
  }
  return out;
}

/**
 * The tiles' click-throughs. The Stores list filters stores, not activities, so each link is the nearest store view:
 * - Visits: stores whose last visit falls in the period (calendar days since the period start);
 * - Contacts / Appointments: the same, with a last visit result that counts as a contact / names an appointment;
 * - Won: stores in a won stage; Open pipeline: stores in an open stage;
 * - Overdue: "Follow-up Needed" (scheduled activity due by tonight, overdue included);
 * - At risk: open stages and no activity for 30 days ("No Activity 30 Days"); No owner: the Unassigned owner.
 * A tile whose filter would be empty (no such results or stages) gets no link rather than the whole list.
 */
export function storeLinks(ctx: StoreLinkContext): StoreLinks {
  const visitDays = Math.max(0, calendarDaysBetween(ctx.range.from, ctx.now));
  const contactIds = ctx.visitResults.filter((r) => r.countsAsContact).map((r) => r.id);
  const appointmentIds = ctx.visitResults.filter((r) => isAppointmentResult(r)).map((r) => r.id);
  const won = stageIds(ctx.pipelines, (s) => s.isWon);
  const open = stageIds(ctx.pipelines, (s) => !s.isWon && !s.isLost);
  return {
    visits: storesHref({ daysSinceVisitMax: visitDays }),
    contacts: contactIds.length > 0 ? storesHref({ visitResultIds: contactIds, daysSinceVisitMax: visitDays }) : undefined,
    appointments:
      appointmentIds.length > 0 ? storesHref({ visitResultIds: appointmentIds, daysSinceVisitMax: visitDays }) : undefined,
    newStores: undefined,
    won: won.length > 0 ? storesHref({ stageIds: won }) : undefined,
    pipeline: open.length > 0 ? storesHref({ stageIds: open }) : undefined,
    overdue: storesHref({ followUpDue: true }),
    atRisk: storesHref(open.length > 0 ? { stageIds: open, daysSinceActivityMin: 30 } : { daysSinceActivityMin: 30 }),
    unassigned: storesHref({ includeUnassigned: true }),
  };
}

// MARK: - KPI tiles

export type KpiId = 'visits' | 'contacts' | 'appointments' | 'newStores' | 'won' | 'pipeline';

export interface KpiTileModel {
  id: KpiId;
  label: string;
  value: string;
  sub?: string;
  href?: string;
  /** Icon badge hex (the iOS StatTile colors). */
  color: string;
}

const KPI_COLORS: Readonly<Record<KpiId, string>> = {
  visits: '#F96A3C',
  contacts: '#2673E6',
  appointments: '#7D4DBF',
  newStores: '#29A85C',
  won: '#F5B400',
  pipeline: '#102A5E',
};

/** `contacts / visits` (0 without visits), as ReportEngine's contact rate. */
export function contactRate(n: Pick<DashboardNumbers, 'visits' | 'contacts'>): number {
  return n.visits > 0 ? n.contacts / n.visits : 0;
}

/** `appointments / contacts` (0 without contacts). */
export function appointmentRate(n: Pick<DashboardNumbers, 'contacts' | 'appointments'>): number {
  return n.contacts > 0 ? n.appointments / n.contacts : 0;
}

/** The six tiles, worded like iOS ReportKPIGrid ("38% contact rate", "40% of contacts", "$4.2K weighted"). */
export function kpiTiles(
  n: DashboardNumbers,
  links: StoreLinks,
  opts: { currencyCode: string; locale?: string },
): KpiTileModel[] {
  const money = (v: number) => formatCompactMoney(v, opts.currencyCode, opts.locale);
  const grouped = (v: number) => formatGrouped(v, opts.locale);
  const tiles: Omit<KpiTileModel, 'color'>[] = [
    { id: 'visits', label: 'Visits', value: grouped(n.visits), href: links.visits },
    {
      id: 'contacts',
      label: 'Contacts',
      value: grouped(n.contacts),
      sub: `${formatPercent(contactRate(n))} contact rate`,
      href: links.contacts,
    },
    {
      id: 'appointments',
      label: 'Appointments',
      value: grouped(n.appointments),
      sub: `${formatPercent(appointmentRate(n))} of contacts`,
      href: links.appointments,
    },
    { id: 'newStores', label: 'New stores', value: grouped(n.newStores), href: links.newStores },
    { id: 'won', label: 'Won', value: grouped(n.won), sub: `${money(n.revenue)} revenue won`, href: links.won },
    {
      id: 'pipeline',
      label: 'Open pipeline',
      value: money(n.open),
      sub: `${money(n.weighted)} weighted · ${grouped(n.openCount)} open ${n.openCount === 1 ? 'store' : 'stores'}`,
      href: links.pipeline,
    },
  ];
  return tiles.map((t) => ({ ...t, color: KPI_COLORS[t.id] }));
}

// MARK: - Activity look (ActivityType.tint, ActivityRow)

const TINTS: Readonly<Record<ActivityType, string>> = {
  visit: '#F96A3C',
  call: '#29A85C',
  email: '#2673E6',
  text: '#009E9E',
  note: '#8C919E',
  meeting: '#7D4DBF',
  task: '#102A5E',
  stageChange: '#F5B400',
  assignment: '#102A5E',
  created: '#29A85C',
  photo: '#7D4DBF',
  document: '#8C919E',
};

/** `ActivityRow.tint`: a visit takes its result's color, everything else its type's tint. */
export function activityTint(
  activity: Pick<ActivityPayload, 'type' | 'resultId'>,
  visitResults: ReadonlyMap<string, Pick<VisitResultPayload, 'colorHex'>>,
): string {
  if (activity.type === 'visit') {
    const result = lookup(visitResults, activity.resultId);
    if (result) return result.colorHex;
  }
  return TINTS[activity.type] ?? TINTS.note;
}

/** `ActivityRow.byLine`: "By Jane Doe", "By Sam Lee (Manager)", "By Unassigned". */
export function byLine(user: Pick<UserPayload, 'firstName' | 'lastName' | 'role'> | undefined): string {
  if (!user) return 'By Unassigned';
  const role = user.role === 'rep' ? '' : ` (${USER_ROLE_TITLES[user.role] ?? user.role})`;
  return `By ${fullName(user)}${role}`;
}

// MARK: - Team now

export interface TeamNowRow {
  user: UserPayload;
  presence: Presence;
  /** Newest breadcrumb time (no position yet = undefined: "No location yet"). */
  lastSeenAt?: Date;
  /** Today's visits / contacts (`repSummary` counts: completed, credited to the person, local calendar day). */
  visits: number;
  contacts: number;
  lastActivity?: { title: string; at: Date; type: ActivityType; tint: string };
  isMe: boolean;
}

export interface TeamNowInput {
  /** The viewer's visible people (`visibleUsers`), or just the rep themself. */
  users: readonly UserPayload[];
  positions: ReadonlyMap<string, RepPosition>;
  /** `teamMetrics(index, dayRange(now))`: today's rows by person. */
  today: readonly MetricsRow[];
  /** `lastActivityByUser(activities)`. */
  lastActivity: ReadonlyMap<string, ActivityPayload>;
  visitResults: ReadonlyMap<string, VisitResultPayload>;
  viewerId: string | null | undefined;
  now: Date;
  /** Keep every active person (a rep's own row). Otherwise reps, plus admins/managers who are out in the field. */
  everyone?: boolean;
}

const PRESENCE_RANK: Readonly<Record<Presence, number>> = { online: 0, away: 1, offline: 2 };

/**
 * "Team now": active people with live presence, today's visits and contacts, and their newest completed activity
 * ("On-Site Visit · Contacted"). Reps always; admins and managers only while they are in the field (a position in the
 * last 8 hours, or a visit, meeting, call, email or text logged today), so office staff do not crowd the list. Online
 * first, then Away, then by today's visits, contacts and name.
 */
export function teamNowRows(input: TeamNowInput): TeamNowRow[] {
  const today = new Map(input.today.map((r) => [idKey(r.profile_id), r]));
  const rows: TeamNowRow[] = [];
  for (const user of input.users) {
    if (!user.isActive) continue;
    const key = idKey(user.id);
    const position = input.positions.get(key);
    const presence = presenceOf(position?.at, input.now);
    const counts = today.get(key);
    const visits = counts?.visits ?? 0;
    const contacts = counts?.contacts ?? 0;
    const logged = counts
      ? counts.visits + counts.appointments + counts.calls + counts.emails + counts.texts > 0
      : false;
    const inField = presence !== 'offline' || logged;
    if (!input.everyone && user.role !== 'rep' && !inField) continue;
    const last = input.lastActivity.get(key);
    const row: TeamNowRow = { user, presence, visits, contacts, isMe: sameId(user.id, input.viewerId) };
    if (position) row.lastSeenAt = position.at;
    if (last) {
      row.lastActivity = {
        title: activityDisplayTitle(last, input.visitResults),
        at: effectiveDate(last),
        type: last.type,
        tint: activityTint(last, input.visitResults),
      };
    }
    rows.push(row);
  }
  return rows.sort(
    (a, b) =>
      PRESENCE_RANK[a.presence] - PRESENCE_RANK[b.presence] ||
      b.visits - a.visits ||
      b.contacts - a.contacts ||
      compareLocalized(fullName(a.user), fullName(b.user)),
  );
}

/** "3 online · 2 away" over the rows (zeros left out; "Nobody in the field" when both are 0). */
export function presenceSummary(rows: readonly Pick<TeamNowRow, 'presence'>[]): string {
  const online = rows.filter((r) => r.presence === 'online').length;
  const away = rows.filter((r) => r.presence === 'away').length;
  const parts = [online > 0 ? `${online} online` : null, away > 0 ? `${away} away` : null].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Nobody in the field';
}

/** RepRow's location line: "Online · 4 minutes ago", "Offline · 2 days ago", or "No location yet". */
export function presenceLine(row: Pick<TeamNowRow, 'presence' | 'lastSeenAt'>, now: Date, locale?: string): string {
  if (!row.lastSeenAt) return 'No location yet';
  const title = row.presence === 'online' ? 'Online' : row.presence === 'away' ? 'Away' : 'Offline';
  return `${title} · ${formatElapsed(row.lastSeenAt, now, locale)}`;
}

/**
 * A last position this old reads as stale, the moment presence stops saying Online. Not sooner: the iPhone records a
 * point only when the rep moves (every 15 m) and uploads about every 5 minutes, so a rep parked in a store for a
 * 20-minute visit sends nothing new while the phone is fine.
 */
export const POSITION_STALE_MS = PRESENCE_ONLINE_MS;

export interface PositionAge {
  /** "as of 2:14 PM" today, "as of Sep 28, 2:14 PM" for an earlier day. */
  text: string;
  /** `POSITION_STALE_MS` old or more: presence is no longer Online. */
  stale: boolean;
}

/** When the rep's last position was taken, so a manager knows how old the dot is (null without a position). */
export function positionAge(lastSeenAt: Date | undefined, now: Date, locale?: string): PositionAge | null {
  if (!lastSeenAt) return null;
  const when = isSameDay(lastSeenAt, now) ? formatShortTime(lastSeenAt, locale) : formatShortDateTime(lastSeenAt, now, locale);
  return { text: `as of ${when}`, stale: presenceOf(lastSeenAt, now) !== 'online' };
}

// MARK: - Leaderboard card

export interface RankedUser {
  rank: number;
  user: UserPayload;
  value: number;
  isMe: boolean;
}

/**
 * `homeTeamSnapshot` with five places: active users ranked by a metric (ties keep the given order), as
 * `leaderboard(metric, period, users)`, but read from one `teamMetrics` pass for the period instead of a pass per
 * person. People with nothing logged are left out, so an empty list means "No team activity yet".
 */
export function topUsers(
  rows: readonly MetricsRow[],
  users: readonly UserPayload[],
  opts: { metric?: GoalMetric; limit?: number; viewerId?: string | null } = {},
): RankedUser[] {
  const metric = opts.metric ?? 'visits';
  const byUser = new Map(rows.map((r) => [idKey(r.profile_id), r]));
  return users
    .filter((u) => u.isActive)
    .map((user) => {
      const row = byUser.get(idKey(user.id));
      return { user, value: row ? metricsRowValue(row, metric) : 0 };
    })
    .sort((a, b) => b.value - a.value)
    .filter((e) => e.value > 0)
    .slice(0, Math.max(0, opts.limit ?? 5))
    .map((e, i) => ({ rank: i + 1, user: e.user, value: e.value, isMe: sameId(e.user.id, opts.viewerId) }));
}

/** "1 visit", "12 visits" (TeamSnapshotRow). */
export function visitsText(n: number, locale?: string): string {
  return `${formatGrouped(n, locale)} ${n === 1 ? 'visit' : 'visits'}`;
}

/** "Rep", "Manager", "Admin". */
export function roleTitle(user: Pick<UserPayload, 'role'>): string {
  return USER_ROLE_TITLES[user.role] ?? user.role;
}

// MARK: - Needs attention

export interface OverdueItem {
  id: string;
  title: string;
  type: ActivityType;
  tint: string;
  recordId?: string;
  recordName?: string;
  /** The second line: the store's name, "Store not available" (archived or not visible) or "No store". */
  storeLabel: string;
  /** `/stores/:id`, only for a store the viewer can open (a link to a hidden or archived one would dead-end). */
  href?: string;
  user?: UserPayload;
  dueAt: Date;
}

/** The store line of an activity row: its name, or why there is none. */
function storeLabel(recordId: string | undefined, recordName: string | undefined): string {
  if (recordName !== undefined) return recordName;
  return recordId ? 'Store not available' : 'No store';
}

export interface AtRiskItem {
  id: string;
  name: string;
  value: number;
  owner?: UserPayload;
  /** Calendar days since `lastActivityAt ?? createdAt`. */
  days: number;
  /** False when nothing was ever logged (the count runs from when the store was added). */
  hadActivity: boolean;
}

export interface NeedsAttention {
  overdue: { count: number; items: OverdueItem[] };
  atRisk: { count: number; value: number; items: AtRiskItem[] };
  /** Visible stores without an owner (null for reps, who cannot assign). */
  unassigned: number | null;
}

export interface NeedsAttentionInput {
  activities: readonly ActivityPayload[];
  /** The viewer's visible, non-archived stores (`visibleRecords`). */
  records: readonly RecordPayload[];
  /** Admins/managers: the visible people. Reps: just themselves. */
  scope: 'team' | 'self';
  visibleUserIds: Iterable<string>;
  viewerId: string | null | undefined;
  stats: DerivedStats;
  pipelines: readonly PipelinePayload[];
  visitResults: ReadonlyMap<string, VisitResultPayload>;
  users: ReadonlyMap<string, UserPayload>;
  now: Date;
  /** Rows per list (default 5). */
  limit?: number;
}

/**
 * Overdue scheduled activities (oldest first) assigned to a visible person, or unassigned on a visible store; open
 * stores with no activity for 30 days (most valuable first, `atRiskRecords`); and visible stores with no owner.
 * Reps see only their own activities and stores, and no unassigned count.
 */
export function needsAttention(input: NeedsAttentionInput): NeedsAttention {
  const limit = input.limit ?? 5;
  const visibleUsers = idSet(input.visibleUserIds);
  const recordsById = new Map(input.records.map((r) => [idKey(r.id), r]));
  const self = input.scope === 'self';

  const overdue = overdueActivities(input.activities, input.now).filter((a) => {
    if (self) return sameId(a.userId, input.viewerId);
    return a.userId ? hasId(visibleUsers, a.userId) : !!a.recordId && recordsById.has(idKey(a.recordId));
  });

  const mine = self ? input.records.filter((r) => sameId(r.ownerId, input.viewerId)) : input.records;
  const atRisk = atRiskRecords(mine, { stats: input.stats, pipelines: input.pipelines, now: input.now });

  return {
    overdue: {
      count: overdue.length,
      items: overdue.slice(0, limit).map((a) => {
        const record = a.recordId ? recordsById.get(idKey(a.recordId)) : undefined;
        const recordName = record ? recordDisplayName(record) : undefined;
        const item: OverdueItem = {
          id: a.id,
          title: activityDisplayTitle(a, input.visitResults),
          type: a.type,
          tint: activityTint(a, input.visitResults),
          storeLabel: storeLabel(a.recordId, recordName),
          dueAt: new Date(ms(a.scheduledAt) ?? 0),
        };
        if (a.recordId) item.recordId = a.recordId;
        if (record && recordName !== undefined) {
          item.recordName = recordName;
          item.href = `/stores/${record.id}`;
        }
        const user = lookup(input.users, a.userId);
        if (user) item.user = user;
        return item;
      }),
    },
    atRisk: {
      count: atRisk.length,
      value: atRisk.reduce((sum, r) => sum + r.value, 0),
      items: atRisk.slice(0, limit).map((r) => {
        const last = input.stats.get(idKey(r.id))?.lastActivityAt;
        const since = last ?? new Date(ms(r.createdAt) ?? input.now.getTime());
        const item: AtRiskItem = {
          id: r.id,
          name: recordDisplayName(r),
          value: r.value,
          days: Math.max(0, calendarDaysBetween(since, input.now)),
          hadActivity: last !== undefined,
        };
        const owner = lookup(input.users, r.ownerId);
        if (owner) item.owner = owner;
        return item;
      }),
    },
    unassigned: self ? null : input.records.filter((r) => !r.ownerId).length,
  };
}

/** "Due 3 days ago" / "Due 20 minutes ago" for an overdue activity. */
export function overdueLine(item: Pick<OverdueItem, 'dueAt'>, now: Date, locale?: string): string {
  return `Due ${formatRelative(item.dueAt, now, locale)}`;
}

/** "No activity for 42 days", or "No activity since added 35 days ago" when nothing was ever logged. */
export function atRiskLine(item: Pick<AtRiskItem, 'days' | 'hadActivity'>): string {
  const days = `${item.days} ${item.days === 1 ? 'day' : 'days'}`;
  return item.hadActivity ? `No activity for ${days}` : `No activity since added ${days} ago`;
}

// MARK: - Recent activity

export interface FeedRow {
  id: string;
  type: ActivityType;
  title: string;
  tint: string;
  recordId?: string;
  recordName?: string;
  /** `/stores/:id` when the store is known to the viewer (ActivityFeedView links rows that have a record). */
  href?: string;
  user?: UserPayload;
  at: Date;
  /** ActivityRowDetails: "From New to Proposal" for a stage change or assignment. */
  transition?: string;
  /** The note preview (three lines on iOS). */
  notes?: string;
  /** ActivityRowDetails: "15 min". */
  duration?: string;
  byLine: string;
  /** The activity's photos and documents, in its order (resolve them against the replica with `activityFiles`). */
  attachmentIds?: string[];
}

/** ActivityRowDetails' transition line: "From New to Proposal" ("—" for an empty side), or undefined. */
export function transitionLine(a: Pick<ActivityPayload, 'type' | 'fromValue' | 'toValue'>): string | undefined {
  if (a.type !== 'stageChange' && a.type !== 'assignment') return undefined;
  const from = a.fromValue ?? '';
  const to = a.toValue ?? '';
  if (from === '' && to === '') return undefined;
  return `From ${from === '' ? '—' : from} to ${to === '' ? '—' : to}`;
}

export interface FeedModel {
  rows: FeedRow[];
  /** Every completed activity in the feed (not just the shown ones). */
  total: number;
  /** How many of them happened today. */
  today: number;
}

/**
 * `teamFeed` newest first, the first `limit` (50) as rows, with ActivityFeedView's header numbers
 * ("142 activities · 12 today"). `userIds` narrows it to one person (a rep's own feed).
 */
export function feedModel(input: {
  activities: readonly ActivityPayload[];
  visibleUsers: readonly UserPayload[];
  users: ReadonlyMap<string, UserPayload>;
  visitResults: ReadonlyMap<string, VisitResultPayload>;
  recordName: (id: string) => string | undefined;
  now: Date;
  userIds?: readonly string[];
  limit?: number;
}): FeedModel {
  const feed = teamFeed(input.activities, input.visibleUsers, input.userIds ? { userIds: input.userIds } : {});
  let today = 0;
  for (const a of feed) {
    // Newest first: once one is before today, the rest are too.
    if (isSameDay(effectiveDate(a), input.now)) today += 1;
    else if (effectiveDate(a).getTime() < input.now.getTime()) break;
  }
  const rows = feed.slice(0, Math.max(0, input.limit ?? 50)).map((a) => {
    const user = lookup(input.users, a.userId);
    const row: FeedRow = {
      id: a.id,
      type: a.type,
      title: activityDisplayTitle(a, input.visitResults),
      tint: activityTint(a, input.visitResults),
      at: effectiveDate(a),
      byLine: byLine(user),
    };
    if (a.recordId) {
      row.recordId = a.recordId;
      const name = input.recordName(a.recordId);
      if (name !== undefined) {
        row.recordName = name;
        row.href = `/stores/${a.recordId}`;
      }
    }
    if (user) row.user = user;
    const transition = transitionLine(a);
    if (transition) row.transition = transition;
    if (a.notes.trim() !== '') row.notes = a.notes.trim();
    if (typeof a.durationMinutes === 'number' && a.durationMinutes > 0) row.duration = `${Math.round(a.durationMinutes)} min`;
    if (a.attachmentIds.length > 0) row.attachmentIds = [...a.attachmentIds];
    return row;
  });
  return { rows, total: feed.length, today };
}

/** "142 activities · 12 today" (ActivityFeedHeader). */
export function feedHeader(model: Pick<FeedModel, 'total' | 'today'>, locale?: string): string {
  return `${formatGrouped(model.total, locale)} ${model.total === 1 ? 'activity' : 'activities'} · ${formatGrouped(model.today, locale)} today`;
}

/**
 * The feed row's time: relative within a day ("19 minutes ago", easier to scan on a live dashboard), else
 * ActivityRow's `effectiveDate.shortDateTime` ("Sep 28, 2:43 PM", the year only when it is not this year).
 */
export function feedTime(at: Date, now: Date, locale?: string): string {
  return now.getTime() - at.getTime() < 86_400_000 && at.getTime() <= now.getTime() + 60_000
    ? formatRelative(at, now, locale)
    : formatShortDateTime(at, now, locale);
}

// MARK: - Photos and documents of an activity

/** An activity's files, split the way the store History entry shows them (ActivityEntry, RecordFilesTab). */
export interface ActivityFiles {
  /** Photos and business cards: the 64 px thumbnails. */
  photos: DataItem<AttachmentPayload>[];
  /** Documents and audio notes: rows with an Open link. */
  documents: DataItem<AttachmentPayload>[];
  /** Distinct attachment ids on the activity. */
  total: number;
  /**
   * Ids with no attachments row in the replica: the iPhone pushed the visit before its photo (offline, still
   * uploading), or the row is one the viewer cannot read. A row whose file never reached storage is not counted
   * here; its tile says "File not available" itself.
   */
  missing: number;
}

/**
 * `activity.attachmentIds` resolved against the synced attachments (`attachments.get`), in the activity's order;
 * null when the activity has none. Only rows the replica holds are shown, so a rep sees exactly what sync gave them.
 */
export function activityFiles(
  attachmentIds: readonly string[] | undefined,
  attachment: (id: string) => DataItem<AttachmentPayload> | undefined,
): ActivityFiles | null {
  if (!attachmentIds || attachmentIds.length === 0) return null;
  const files: ActivityFiles = { photos: [], documents: [], total: 0, missing: 0 };
  const seen = new Set<string>();
  for (const id of attachmentIds) {
    // A repeated id would draw the same tile twice (and repeat a React key).
    if (seen.has(idKey(id))) continue;
    seen.add(idKey(id));
    files.total += 1;
    const item = attachment(id);
    if (!item) files.missing += 1;
    else if (item.value.kind === 'document' || item.value.kind === 'audio') files.documents.push(item);
    else files.photos.push(item);
  }
  return files;
}

/**
 * ActivityEntry's paperclip line when files have not all arrived: "2 attachments" when none has, "3 attachments
 * (1 not available)" when some have; null when every file is here.
 */
export function missingFilesText(files: Pick<ActivityFiles, 'total' | 'missing'>): string | null {
  if (files.missing === 0) return null;
  const count = attachmentCountText(files.total);
  return files.missing === files.total ? count : `${count} (${files.missing} not available)`;
}

/** The rows under a photo in the viewer (AttachmentDetailSheet, as on the store page): Added, By, Logged with. */
export function photoDetails(
  attachment: Pick<AttachmentPayload, 'createdAt' | 'uploadedBy'>,
  opts: { users: ReadonlyMap<string, UserPayload>; loggedWith?: string; now: Date; locale?: string },
): { label: string; value: string | null }[] {
  const added = parseWireDate(attachment.createdAt);
  const rows = [
    { label: 'Added', value: added ? formatShortDateTime(added, opts.now, opts.locale) : null },
    { label: 'By', value: userName(opts.users, attachment.uploadedBy) },
  ];
  if (opts.loggedWith) rows.push({ label: 'Logged with', value: opts.loggedWith });
  return rows;
}

/** The type's title for screen readers when the row has no text of its own. */
export function activityTypeTitle(type: ActivityType): string {
  return ACTIVITY_TYPE_TITLES[type] ?? type;
}
